import { ComponentProps } from "react";
import CompetitionCardFooter from "../components/CompetitionDetails/CompetitionCardFooter";
import {
  PageContext,
  isCompetitionPageContext,
} from "../hooks/useDetailsPageContext";
import EventCTACard from "../components/events.EventCTACard";
import { CTACountdownProps } from "../components/events.CTACard";
import { formatPrizeNameWithArticle } from "~features/competitions/helpers";

const competitionCTACardProps = (pageContext: PageContext) => {
  if (!isCompetitionPageContext(pageContext)) {
    return {};
  }

  return {
    title: pageContext.competition?.title,
    image: pageContext.competition?.prizes?.[0].image,
    imageAlt: pageContext.competition?.title,
    footer: (
      <CompetitionCardFooter
        competition={pageContext.competition}
        venue={pageContext.event?.venue}
      />
    ),
  };
};

interface EventCTAState {
  ctaTitle: string | null;
  competitionCTACardProps: ComponentProps<typeof EventCTACard>;
  countdownTo?: CTACountdownProps["countdownTo"];
  forceUseCtaTitle: boolean;
}

const eventCTATitle = (pageContext: PageContext) => {
  switch (pageContext.state) {
    case "info":
      return "The countdown is on!";
    case "registration":
    case "registration-presale":
      return "Be the first to know when tickets go live!";
    case "active-presale":
      return "Presale is now live!";
    case "onsale":
      return "Secure your tickets today!";
    case "registration-presale-pre-purchase":
      return "Be first in line for tickets!";
    case "registration-presale-pre-purchase-competition": {
      const prizeNameWithArticle = formatPrizeNameWithArticle(
        pageContext.competition
      );
      return `Register and be in to win ${prizeNameWithArticle ?? "a prize"}!`;
    }
    case "registration-competition":
    case "registration-presale-competition":
    case "onsale-competition":
      return pageContext.competition?.title;
  }
};

export const ctaStateFromPageContext = (
  pageContext: PageContext
): EventCTAState => {
  const presaleStartTime = pageContext.event?.presaleRelease?.startConditionDate
    ? new Date(pageContext.event?.presaleRelease?.startConditionDate)
    : null;

  const mainSaleStartTime = pageContext.event?.publicReleaseStartsAt
    ? new Date(pageContext.event.publicReleaseStartsAt)
    : null;

  let countdownTo: EventCTAState["countdownTo"] | undefined =
    mainSaleStartTime &&
    (pageContext.state === "active-presale" ||
      pageContext.state === "registration" ||
      pageContext.state === "registration-competition" ||
      pageContext.state === "info")
      ? {
          type: "onsale",
          date: mainSaleStartTime,
          label: "Public sale starts:",
        }
      : undefined;

  if (
    presaleStartTime &&
    (pageContext.state === "registration-presale" ||
      pageContext.state === "registration-presale-competition" ||
      pageContext.state === "registration-presale-pre-purchase" ||
      pageContext.state === "registration-presale-pre-purchase-competition")
  ) {
    // Hack: Twomindsfestival ballot 2. Can be removed December 2025.
    if (pageContext.event?.id === "5099cea1-218d-4dc7-8b1e-a1d3c42a4c41") {
      countdownTo = {
        type: "presale",
        date: presaleStartTime,
        label: "Ballot 2 starts:",
      };
    } else {
      countdownTo = {
        type: "presale",
        date: presaleStartTime,
        label: "Presale starts:",
      };
    }
  }

  return {
    ctaTitle: eventCTATitle(pageContext),
    countdownTo,
    competitionCTACardProps: competitionCTACardProps(pageContext),
    forceUseCtaTitle:
      pageContext.state === "registration-presale-pre-purchase-competition",
  };
};
